/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from "@hookform/resolvers/yup";
import { FilesUploader, If } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { ProductsFieldForForms } from "modules/products";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FormField, Input, MediaFilePicker } from "ui";

import { FeedInsertType } from "../../../constants";
import { feedInsertValidationSchema } from "../../../helpers/validations";
import {
  InsertFormField,
  InsertMediasField,
  InsertTypeField,
  SliderBackgroundImageField
} from "./atoms";
import {
  FeedInsertFormProps,
  FeedInsertFormValues
} from "./FeedInsertForm.types";

import styles from "./FeedInsertForm.module.scss";

const FeedInsertForm = ({
  formId,
  onSubmit,
  defaultValues
}: FeedInsertFormProps) => {
  const form = useForm<FeedInsertFormValues>({
    defaultValues,
    resolver: yupResolver(feedInsertValidationSchema) as any
  });
  const {
    control,
    handleSubmit,
    register,
    watch,
    formState: { errors }
  } = form;

  const type = watch("type");

  return (
    <FormProvider {...form}>
      <form
        id={formId}
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Input
          {...register("name")}
          errorMessage={errors.name?.message}
          placeholder={"Enter Insert Name"}
          label={"Name (for admins, users won’t see this)"}
        />

        <InsertFormField />

        <InsertTypeField />

        <Input
          {...register("header")}
          errorMessage={errors.header?.message}
          placeholder={"Enter Title"}
          label={"Title (optional)"}
        />

        <Input
          asTag={"textarea"}
          {...register("text")}
          errorMessage={errors.text?.message}
          placeholder={"Enter Description"}
          label={"Description (optional)"}
        />

        <If
          condition={
            type === FeedInsertType.MultipleMedia ||
            type === FeedInsertType.Products
          }
        >
          <div className={styles.form_backgroundMediasFields}>
            <Controller
              control={control}
              name="headerBackgroundMedia"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <FormField
                  label={"Slider header image (optional)"}
                  errorMessage={error?.message}
                >
                  <FilesUploader
                    onSuccessUploading={onChange}
                    uploadDirectoryKey={MediaDirectoryKeys.ProductFeedInsert}
                  >
                    {(uploaderProps) => (
                      <MediaFilePicker
                        {...uploaderProps}
                        value={value}
                        allowedMediaTypes={[MediaType.IMAGE]}
                        customSize={{ width: 375, height: 64 }}
                        label={"375:64"}
                        onDeleteMediaFile={() => onChange(null)}
                      />
                    )}
                  </FilesUploader>
                </FormField>
              )}
            />

            <SliderBackgroundImageField />

            <Controller
              control={control}
              name="spaceBeforeSliderStart"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Input
                  value={value ?? ""}
                  onChange={onChange}
                  errorMessage={error?.message}
                  placeholder={"Enter Space Before Slider Start"}
                  label={"Extra Space Before Slider Start (%)"}
                  type="number"
                  min={0}
                  max={100}
                  maxLength={3}
                  onlyInteger
                />
              )}
            />
          </div>
        </If>

        <If
          condition={
            type === FeedInsertType.SingleMedia ||
            type === FeedInsertType.MultipleMedia
          }
        >
          <InsertMediasField />
        </If>

        <If condition={type === FeedInsertType.Products}>
          <Controller
            control={form.control}
            name={"products"}
            render={({
              field: { value: productsList, onChange: onChangeProductsList }
            }) => (
              <Controller
                control={form.control}
                name={"productSet"}
                render={({
                  field: { value: productSet, onChange: onChangeProductSet }
                }) => (
                  <ProductsFieldForForms
                    productsList={productsList}
                    onChangeProductsList={onChangeProductsList}
                    productSet={productSet}
                    onChangeProductSet={onChangeProductSet}
                    errorMessage={
                      errors.products?.message || errors.productSet?.message
                    }
                  />
                )}
              />
            )}
          />
        </If>
      </form>
    </FormProvider>
  );
};

export default FeedInsertForm;
